import React, { useState, useCallback } from 'react';
import {
    Box, Button, Typography, Paper, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, CircularProgress, Collapse, Card,
    CardContent, Divider, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Papa from 'papaparse';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;


const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
}));

dayjs.extend(utc);
dayjs.extend(timezone);

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();
  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2, mb: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
          fontSize: "0.9rem",
          color: "#11224d",
          mb: 0.5,
          borderBottom: '1px solid #ddd',
          pb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
);

const PreviewRow = ({ load, index }) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                    <IconButton size="small">
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{load.origin}</TableCell>
                <TableCell>{load.destination}</TableCell>
                <TableCell>{dayjs(load.pickupDate).format('MM/DD/YYYY')}</TableCell>
                <TableCell>{load.equipment}</TableCell>
                <TableCell>{load.company_name} ({load.posted_by_role})</TableCell>
               <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', textAlign: 'center' }}>
                 {load.offerRate
                   ? (load.rateType === 'Flat'
                       ? `$${Number(load.offerRate).toFixed(0)}`
                       : `$${Number(load.offerRate).toFixed(2)}/mi`)
                   : '–'}
               </TableCell>
                <TableCell>{load.tripMiles || 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Card sx={{ border: "1px solid #f0f0f0", borderRadius: 1, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", backgroundColor: "#ffffff" }}>
                            <CardContent sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, fontSize: "1rem", color: "#11224d" }}>
                                    {load.origin} → {load.destination} ({load.tripMiles ? Math.round(Number(load.tripMiles)) : "N/A"} mi)
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
                                    <Box sx={{ flex: '1 1 50%', pr: 2 }}>
                                        <DetailSection title="Trip Details">
                                            <Typography variant="caption" display="block"><b>Equipment:</b> {load.equipment || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Load Type:</b> {load.fullOrPartial || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Feet:</b> {load.length ? `${load.length} ft` : "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Weight:</b> {load.weight ? `${load.weight} lbs` : "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Commodity:</b> {load.commodity || "N/A"}</Typography>
                                            <Typography variant="caption" display="block"><b>Notes:</b> {load.comment || "N/A"}</Typography>
                                            
                                        </DetailSection>
                                    </Box>
                                    <Box sx={{ flex: '1 1 50%', pr: 2 }}>
                                        <DetailSection title="Rate/ Compnay Details">
                                             <Typography variant="caption" sx={{ display: "block" }}>
                                                            <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                                                                Offer Rate ({load.rateType === "Flat" ? "Flat" : "Per Mile"}):
                                                            </span>{" "}
                                                            {load.offerRate
                                                                ? `$${Number(load.offerRate).toFixed(2)}`
                                                                : "-"}
                                                        </Typography>
                                            
                                                       
                                                        <Typography variant="caption" sx={{ display: "block" }}>
                                                            <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                                                                Rate / Mile:
                                                            </span>{" "}
                                                            {load.tripMiles && load.offerRate ? (
                                                                load.rateType === "Flat"
                                                                    ? `$${(Number(load.offerRate) / Number(load.tripMiles)).toFixed(2)}`
                                                                    : `$${Number(load.offerRate).toFixed(2)}`
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </Typography>
                                            
                                            <Typography variant="caption" display="block"><b>Rate Type:</b> {load.rateType|| "N/A"}</Typography>
                                           
                                            <Typography variant="caption" display="block"><b>Payment Method:</b> {load.paymentMethod ? `${load.paymentMethod}` : "N/A"}</Typography>
                                            
                                             <Typography variant="caption" display="block"><b>Contact:</b> {load.contactMethod? `${load.contactMethod}` : "N/A"}</Typography>
                                             {load.posted_by_role === 'broker' && (
                                                <Typography variant="caption" display="block">
                                                    <b>{getRegistrationType(load.registration_number)}:</b> {load.registration_number || "N/A"}
                                                </Typography>
                                             )}
                                            
                                        </DetailSection>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const AdminBulkLoadPosting = () => {
    const [file, setFile] = useState(null);
    const [parsedLoads, setParsedLoads] = useState([]);
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadResponse, setUploadResponse] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type === 'text/csv') {
            setFile(selectedFile);
            setParsedLoads([]);
            setErrors([]);
            setUploadResponse(null);
        } else {
            setErrors([{ index: 'File', message: 'Please select a valid CSV file.' }]);
        }
    };

    const validateLocation = async (address) => {
        if (!address || !MAPBOX_ACCESS_TOKEN) return null;
        const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`;
        try {
            const response = await axios.get(endpoint, {
                params: {
                    access_token: MAPBOX_ACCESS_TOKEN,
                    limit: 1,
                    types: 'place,postcode,address',
                    country: 'US,CA,MX',
                    autocomplete: true
                }
            });
            const features = response.data.features;
            if (features.length > 0) {
                const feature = features[0];
                let city = feature.text;
                let state = "";

                if (feature.context) {
                    const region = feature.context.find(c => c.id.startsWith('region'));
                    if (region) {
                        state = region.short_code ? region.short_code.split('-')[1] : region.text;
                    }

                    if (feature.place_type.includes('postcode')) {
                        const place = feature.context.find(c => c.id.startsWith('place'));
                        if (place) city = place.text;
                    }
                }

                return {
                    place_name: (city && state) ? `${city}, ${state}` : feature.place_name,
                    center: feature.center 
                };
            }
        } catch (error) {
            console.error("Error validating address:", address, error);
        }
        return null;
    };

    const getDistance = async (originCoords, destCoords) => {
        if (!originCoords || !destCoords || !MAPBOX_ACCESS_TOKEN) return null;
        const coordinates = `${originCoords[0]},${originCoords[1]};${destCoords[0]},${destCoords[1]}`;
        const directionsEndpoint = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}`;
        try {
            const response = await axios.get(directionsEndpoint, {
                params: {
                    access_token: MAPBOX_ACCESS_TOKEN,
                    geometries: 'polyline',
                }
            });
            if (response.data.routes && response.data.routes.length > 0) {
                const distanceInMeters = response.data.routes[0].distance;
                return Math.round(distanceInMeters * 0.000621371);
            }
        } catch (error) {
            console.error("Error calculating distance:", error);
        }
        return null;
    };

    const handleParse = useCallback(async () => {
        if (!file) return;

        setLoading(true);
        setErrors([]);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                const validationErrors = [];
                const processedLoads = [];

                for (const [index, row] of results.data.entries()) {
                    const requiredFields = ['origin', 'destination', 'pickupDate', 'equipment', 'company_name', 'posted_by_role'];

                    const cleanRow = Object.keys(row).reduce((acc, key) => {
                        const val = row[key];
                        acc[key.trim().toLowerCase()] = val ? String(val).trim() : '';
                        return acc;
                    }, {});

                    const load = {};
                    let hasError = false;

                    for (const field of requiredFields) {
                        const lowerCaseField = field.toLowerCase();
                        if (!cleanRow[lowerCaseField]) {
                            validationErrors.push({ index, message: `Row ${index + 1}: Missing required field '${field}'.` });
                            hasError = true;
                        } else {
                            load[field] = cleanRow[lowerCaseField];
                        }
                    }

                    if (hasError) continue;

                  
                    let role = load['posted_by_role'].toLowerCase();
                    if (role === 's' || role === 'shipper') load['posted_by_role'] = 'shipper';
                    else if (role === 'b' || role === 'broker') load['posted_by_role'] = 'broker';

                    const optionalFields = ['length', 'weight', 'offerRate', 'rateType', 'commodity', 'comment', 'fullOrPartial', 'paymentMethod', 'contactMethod', 'referenceId'];
                    optionalFields.forEach(field => {
                        const lowerCaseField = field.toLowerCase();
                        load[field] = cleanRow[lowerCaseField] || null;
                    });

                    
                    const mcValue = cleanRow['mc_number'] || cleanRow['mc_numbr'] || null;
                    load.registration_number = mcValue;
                    load.mc_number = mcValue;
                    if (load.posted_by_role !== 'broker') {
                        load.registration_number = null;
                        load.mc_number = null;
                    }

                    
                    if (load.fullOrPartial) {
                        const fp = load.fullOrPartial.toLowerCase();
                        if (fp === 'full') load.fullOrPartial = 'Full';
                        else if (fp === 'partial') load.fullOrPartial = 'Partial';
                        else if (fp === 'full or partial') load.fullOrPartial = 'Full or Partial';
                    }

                   
                    try {
                        const [originData, destData] = await Promise.all([
                            validateLocation(load.origin),
                            validateLocation(load.destination)
                        ]);

                        if (originData) load.origin = originData.place_name;
                        if (destData) load.destination = destData.place_name;

                        if (originData && destData) {
                            load.tripMiles = await getDistance(originData.center, destData.center);
                        } else {
                            load.tripMiles = null;
                        }
                    } catch (e) {
                        console.error("Error enriching load data", e);
                    }

                    load.timePosted = new Date().toISOString(); 

                    processedLoads.push(load);
                }

                if (validationErrors.length > 0) {
                    setErrors(validationErrors);
                }
                setParsedLoads(processedLoads);
                setLoading(false);
            }
        });
    }, [file]);

    const handlePostLoads = async () => {
        if (parsedLoads.length === 0) {
            setErrors([{ index: 'Validation', message: 'No valid loads to post.' }]);
            return;
        }

        setLoading(true);
        setUploadResponse(null);
        setErrors([]);

        try {
            const token = localStorage.getItem('jwtToken');
            const response = await axios.post(`${API_BASE_URL}/api/admin/loads/bulk`, { loads: parsedLoads }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUploadResponse(response.data);
            setParsedLoads([]); 
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'An error occurred during bulk posting.';
            setErrors([{ index: 'API', message: errorMessage }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <StyledPaper>
                <Typography variant="h6" gutterBottom>Bulk Post Loads via CSV</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload a CSV file with load data. Required headers are: `origin`, `destination`, `pickupDate`, `equipment`, `company_name`, and `posted_by_role` (shipper/broker). Other optional headers like `weight`, `length`, `offerRate` will also be processed.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="contained" component="label">
                        Choose CSV File
                        <input type="file" hidden accept=".csv" onChange={handleFileChange} />
                    </Button>
                    {file && <Typography variant="body2">{file.name}</Typography>}
                    <Button onClick={handleParse} disabled={!file || loading} variant="outlined">
                        Preview Loads
                    </Button>
                </Box>
            </StyledPaper>

            {loading && <CircularProgress sx={{ display: 'block', margin: '2rem auto' }} />}

            {errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography fontWeight="bold">Validation Errors:</Typography>
                    {errors.map((e, i) => <Typography key={i} variant="body2">- {e.message}</Typography>)}
                </Alert>
            )}

            {uploadResponse && (
                <Alert severity={uploadResponse.errors?.length > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
                    {uploadResponse.message}
                    {uploadResponse.errors?.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                            <Typography fontWeight="bold">Details:</Typography>
                            {uploadResponse.errors.map((e, i) => (
                                <Typography key={i} variant="body2">- Row {e.index + 1}: {e.message}</Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            {parsedLoads.length > 0 && !loading && (
                <>
                    <Typography variant="h6" gutterBottom>Load Preview</Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead sx={{ backgroundColor: '#11224d' }}>
                                <TableRow>
                                    <TableCell />
                                    <TableCell sx={{ color: 'white' }}>Origin</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Destination</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Pickup</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Equipment</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Company</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Rate</TableCell>
                                    <TableCell sx={{ color: 'white' }}>Miles</TableCell>
                                </TableRow>
                                
                            </TableHead>
                            <TableBody>
                                {parsedLoads.map((load, index) => (
                                    <PreviewRow key={index} load={load} index={index} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" color="primary" onClick={handlePostLoads} disabled={loading}>
                            Confirm and Post {parsedLoads.length} Loads
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminBulkLoadPosting;